import { useCallback, useEffect, useState } from 'react'
import {
  changerStatutMarchand,
  chargerDossierMarchand,
  enregistrerIdentiteMarchand,
  rejeterPiece,
  validerPiece,
  verifierCompteReversement,
} from '../../api/candidatures'
import { signatureCourante, signatureDocumensoEnCours } from '../../onboarding/contrats'
import { actionsStatut, etapesMarchand, prochaineActionStaff, statutAvantArret } from '../../onboarding/etapes'
import { messageErreur } from '../../onboarding/erreurs'
import { piecesManquantes } from '../../onboarding/pieces'
import { manquantsDeLaSaisie, piecesRequisesMarchand, saisieDepuis, type StatutJuridique } from '../../onboarding/identite'
import {
  LIBELLE_DOC_MARCHAND,
  PIECES_MARCHAND_FACULTATIVES,
  type CandidatureMarchand,
  type CompteReversement,
  type DossierMarchand,
} from '../../onboarding/types'
import { dateCourte, dateComplete } from '../Format'
import { Ligne } from '../Modale'
import {
  Bloc,
  BadgeStatut,
  BlocDecision,
  Erreur,
  Info,
  Journal,
  ProchaineAction,
  Stepper,
  useAction,
} from './Briques'
import { BlocIdentite } from './BlocIdentite'
import { ListePieces } from './ListePieces'
import { SectionContrat } from './SectionContrat'

const LIBELLE_FOURNISSEUR: Record<string, string> = { wave: 'Wave', orange_money: 'Orange Money' }

/** Fiche dossier d'un commerçant (contenu du panneau latéral large). */
export function FicheMarchand({ cand, surChange }: { cand: CandidatureMarchand; surChange: () => void }) {
  const [dossier, setDossier] = useState<DossierMarchand | null>(null)
  const [erreurLecture, setErreurLecture] = useState<string | null>(null)
  const [statutChoisi, setStatutChoisi] = useState<StatutJuridique | null>(null)

  const recharger = useCallback(async () => {
    try {
      setDossier(await chargerDossierMarchand(cand.id))
      setErreurLecture(null)
    } catch (e) {
      setErreurLecture(messageErreur(e))
    }
  }, [cand.id])

  useEffect(() => {
    setDossier(null)
    void recharger()
  }, [recharger])

  const apres = useCallback(async () => {
    await recharger()
    surChange()
  }, [recharger, surChange])

  if (erreurLecture) return <Erreur texte={erreurLecture} />
  if (!dossier) return <p className="dos-note">Chargement du dossier…</p>

  const pieces = dossier.pieces
  // Les pièces exigées SUIVENT le statut juridique (même règle que la base : marchand_pieces_manquantes).
  // Le statut CHOISI dans le formulaire prime sur celui enregistré : une personne physique ne voit jamais
  // RCCM ni NIF exigés, même avant d'avoir cliqué « Enregistrer ».
  const statutJuridique = statutChoisi ?? (dossier.identite?.['statut_juridique'] as StatutJuridique | undefined) ?? null
  const requises = piecesRequisesMarchand(statutJuridique)
  const identiteManquante = manquantsDeLaSaisie('commercant', saisieDepuis(dossier.identite))
  const action = prochaineActionStaff('commercant', cand.statut, {
    piecesEnAttente: pieces.filter((p) => p.statut === 'en_attente').length,
    piecesManquantes: piecesManquantes(requises, pieces).length,
    identiteManquante: identiteManquante.length,
    compteAbsent: dossier.compte === null,
    compteAVerifier: dossier.compte !== null && !dossier.compte.verifie,
    signatureEnCours: signatureDocumensoEnCours(dossier.signatures) !== null,
  })
  const signee = signatureCourante(dossier.signatures)

  return (
    <>
      <Bloc titre="Établissement" droite={<BadgeStatut statut={cand.statut} />}>
        <Ligne libelle="Nom">{cand.nom}</Ligne>
        <Ligne libelle="Type">{cand.type}</Ligne>
        <Ligne libelle="Quartier">{cand.quartier || '—'}</Ligne>
        <Ligne libelle="Gérant">{cand.nomGerant || '—'}</Ligne>
        <Ligne libelle="E-mail du gérant">{cand.emailGerant || '—'}</Ligne>
        <Ligne libelle="Téléphone">{cand.telephone || '—'}</Ligne>
        <Ligne libelle="Créé le">{dateCourte(cand.creeLe)}</Ligne>
        {cand.motif && (cand.statut === 'refuse' || cand.statut === 'suspendu') && (
          <Ligne libelle="Motif">{cand.motif}</Ligne>
        )}
      </Bloc>

      <Bloc titre="Parcours">
        <Stepper etapes={etapesMarchand(cand.statut, statutAvantArret(dossier.journal))} />
        <ProchaineAction action={action} />
      </Bloc>

      <Bloc titre="Statut juridique et identité">
        <BlocIdentite
          sujet="commercant"
          ligne={dossier.identite}
          nomEtablissement={cand.nom}
          surStatut={setStatutChoisi}
          surEnregistrer={async (saisie) => {
            const r = await enregistrerIdentiteMarchand(cand.id, saisie)
            await apres()
            return r
          }}
        />
      </Bloc>

      <Bloc titre="Pièces du dossier">
        {statutJuridique === null && (
          <p className="dos-note" style={{ marginBottom: 6 }}>
            Statut juridique pas encore choisi : pièces de la règle d'origine (RCCM, NIF…). Elles s'adapteront au statut.
          </p>
        )}
        <ListePieces
          pieces={pieces}
          requises={requises}
          facultatives={PIECES_MARCHAND_FACULTATIVES}
          libelles={LIBELLE_DOC_MARCHAND}
          avertirValidite={false}
          surValider={async (p) => {
            await validerPiece('merchant_documents', p.id)
            await apres()
          }}
          surRejeter={async (p, motif) => {
            await rejeterPiece('merchant_documents', p.id, motif)
            await apres()
          }}
        />
      </Bloc>

      <Bloc titre="Compte de reversement">
        <CompteReversementBloc
          compte={dossier.compte}
          surVerifier={async () => {
            await verifierCompteReversement(cand.id)
            await apres()
          }}
        />
      </Bloc>

      <Bloc titre="Contrat et exclusivité">
        <SectionContrat
          kind="commercant"
          signerId={cand.proprietaireId}
          merchantId={cand.id}
          nomSignataire={cand.nomGerant}
          telephone={cand.telephone}
          email={cand.emailGerant}
          signatures={dossier.signatures}
          surChange={() => void apres()}
        />
        {cand.statut === 'documents' && (
          <p className="dos-note" style={{ marginTop: 8 }}>
            Pour mettre le contrat à disposition du gérant dans son application, passez le dossier à « Contrat envoyé »
            (bouton « Envoyer le contrat » ci-dessous).
          </p>
        )}
        {signee?.statut === 'signe' && !signee.exclusiviteDebut && (
          <div className="dos-alerte">L'exclusivité n'est pas enregistrée sur cette signature : l'activation sera refusée.</div>
        )}
      </Bloc>

      <Bloc titre="Décision">
        <BlocDecision
          actions={actionsStatut('commercant', cand.statut)}
          surDecider={async (vers, motif) => {
            await changerStatutMarchand(cand.id, vers, motif)
            await apres()
          }}
        />
      </Bloc>

      <Bloc titre="Journal du dossier">
        <Journal evenements={dossier.journal} />
      </Bloc>
    </>
  )
}

function CompteReversementBloc({
  compte,
  surVerifier,
}: {
  compte: CompteReversement | null
  surVerifier: () => Promise<void>
}) {
  const { occupe, erreur, info, lancer } = useAction()
  if (!compte) {
    return <p className="dos-note">Le gérant n'a pas encore saisi de compte de reversement (Wave ou Orange Money).</p>
  }
  return (
    <>
      <Ligne libelle="Titulaire">{compte.titulaire}</Ligne>
      <Ligne libelle="Opérateur">{LIBELLE_FOURNISSEUR[compte.fournisseur] ?? compte.fournisseur}</Ligne>
      <Ligne libelle="Numéro">{compte.telephone}</Ligne>
      <Ligne libelle="Vérification">
        {compte.verifie ? (
          <span className="badge badge-green">Vérifié{compte.verifieLe ? ` le ${dateComplete(compte.verifieLe)}` : ''}</span>
        ) : (
          <span className="badge badge-yellow">À vérifier</span>
        )}
      </Ligne>
      {!compte.verifie && (
        <>
          <p className="dos-note" style={{ marginTop: 6 }}>
            Vérifiez que le nom du titulaire correspond bien au gérant : ce compte reçoit les reversements.
          </p>
          <div className="dos-champs">
            <button
              className="btn btn-green btn-sm"
              disabled={occupe}
              onClick={() =>
                void lancer(async () => {
                  await surVerifier()
                  return 'Compte vérifié.'
                })
              }
            >
              Vérifier le compte
            </button>
          </div>
        </>
      )}
      <Erreur texte={erreur} />
      <Info texte={info} />
    </>
  )
}
